import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getActiveUsersSummary, getActiveUsersTimeseries, getActiveUsersByRegion } from '../../services/analyticsService';
import { format, parseISO, subDays } from 'date-fns';
import Groups from '@mui/icons-material/Groups';
import TrendingUp from '@mui/icons-material/TrendingUp';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Public from '@mui/icons-material/Public';
import CustomPagination from '../../components/common/CustomPagination.js';

const REPORT_TZ = 'Africa/Lagos';

const UserActivity = () => {
  const theme = useTheme();
  const today = format(new Date(), 'yyyy-MM-dd');
  const defaultFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [byRegion, setByRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionPagination, setRegionPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0,
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const regionRows = byRegion?.by_region || [];
  const totalRegionRows = regionRows.length;
  const totalPages = Math.ceil(totalRegionRows / regionPagination.rowsPerPage) || 1;

  const loadSummary = useCallback(async () => {
    try {
      const data = await getActiveUsersSummary({ date });
      setSummary(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load summary');
    }
  }, [date]);

  const loadTimeseries = useCallback(async () => {
    try {
      const data = await getActiveUsersTimeseries({ date_from: dateFrom, date_to: dateTo });
      setTimeseries(data);
    } catch (e) {
      if (!error) setError(e.response?.data?.detail || e.message || 'Failed to load timeseries');
    }
  }, [dateFrom, dateTo]);

  const loadByRegion = useCallback(async () => {
    try {
      const data = await getActiveUsersByRegion({ date });
      setByRegion(data);
    } catch (e) {
      if (!error) setError(e.response?.data?.detail || e.message || 'Failed to load by region');
    }
  }, [date]);

  useEffect(() => {
    setError(null);
    setLoading(true);
    Promise.all([loadSummary(), loadTimeseries(), loadByRegion()]).finally(() => setLoading(false));
  }, [loadSummary, loadTimeseries, loadByRegion]);

  useEffect(() => {
    setRegionPagination((prev) => ({
      ...prev,
      totalCount: totalRegionRows,
    }));
  }, [totalRegionRows]);

  const regionColumns = useMemo(
    () => [
      {
        field: 'sn',
        headerName: 'S/N',
        width: 80,
        sortable: false,
        filterable: false,
      },
      {
        field: 'region_name',
        headerName: 'Region',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          const label = row.region_code ? `${row.region_name} (${row.region_code})` : row.region_name;
          return label;
        },
      },
      {
        field: 'dau',
        headerName: 'DAU',
        width: 100,
        sortable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'wau',
        headerName: 'WAU',
        width: 100,
        sortable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'mau',
        headerName: 'MAU',
        width: 100,
        sortable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
    ],
    []
  );

  const paginatedRegionRows = useMemo(() => {
    const start = regionPagination.page * regionPagination.rowsPerPage;
    const end = start + regionPagination.rowsPerPage;
    return regionRows.slice(start, end).map((row, index) => ({
      ...row,
      id: row.region_id ?? `unknown-${index}`,
      sn: start + index + 1,
    }));
  }, [regionRows, regionPagination.page, regionPagination.rowsPerPage]);

  const handleRegionPageChange = (newPage) => {
    setRegionPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRegionRowsPerPageChange = (e) => {
    const v = e?.target?.value;
    if (v === 'custom') {
      setCustomRowsActive(true);
      return;
    }
    const num = Number(v);
    if (!isNaN(num) && num > 0) {
      setRegionPagination((prev) => ({ ...prev, rowsPerPage: num, page: 0 }));
    }
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0 && value <= 1000) {
      setRegionPagination((prev) => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  if (loading && !summary && !timeseries && !byRegion) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const chartData = timeseries?.series?.map((d) => ({
    ...d,
    dateLabel: format(parseISO(d.date), 'MMM d'),
  })) || [];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: '#DC143C',
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          }}
        >
          User activity (DAU / WAU / MAU)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dates are in {REPORT_TZ}. Activity = login or session/token refresh.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Summary date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Chart from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Chart to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>Daily Active Users</Typography>
                  <Typography variant="h4">{summary?.dau ?? '—'}</Typography>
                  <Typography variant="caption" color="textSecondary">{summary?.date}</Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>Weekly Active Users</Typography>
                  <Typography variant="h4">{summary?.wau ?? '—'}</Typography>
                  <Typography variant="caption" color="textSecondary">Last 7 days</Typography>
                </Box>
                <Groups sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>Monthly Active Users</Typography>
                  <Typography variant="h4">{summary?.mau ?? '—'}</Typography>
                  <Typography variant="caption" color="textSecondary">Last 30 days</Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            DAU / WAU / MAU over time
          </Typography>
          <Box sx={{ width: '100%', height: 320 }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.date}
                    formatter={(value) => [value, '']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="dau" name="DAU" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="wau" name="WAU" stroke={theme.palette.info.main} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="mau" name="MAU" stroke={theme.palette.success.main} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                No data for selected range
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
            <Public /> By region ({byRegion?.date ?? date})
          </Typography>
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              minHeight: { xs: 400, sm: 520 },
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
            }}
          >
            <DataGrid
              rows={paginatedRegionRows}
              columns={regionColumns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 500,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem',
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 3, px: 2, pb: 2 }}>
            <CustomPagination
              page={regionPagination.page}
              totalPages={totalPages}
              rowsPerPage={regionPagination.rowsPerPage}
              onPageChange={handleRegionPageChange}
              onRowsPerPageChange={handleRegionRowsPerPageChange}
              customRowsActive={customRowsActive}
              customRowsInput={customRowsInput}
              onCustomRowsInputChange={(e) => handleCustomRowsInputChange(e.target.value)}
              onCustomRowsInputBlur={handleCustomRowsInputBlur}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserActivity;
